/**
 * InvestigationPage - Chat-style Investigation Interface
 *
 * Features:
 * - Conversational UI (User Query -> AI Response)
 * - Centered "ChatGPT-like" layout
 * - Embedded rich content (Graph, Root Cause)
 * - Value-add bottom input bar
 */

import { useState, useEffect, useRef } from 'react';
import {
  Microscope, Clock, ArrowLeft, RefreshCw, Loader2, AlertCircle,
  CheckCircle, XCircle, ExternalLink, FileCode, GitPullRequest,
  Terminal, ChevronRight, Copy, CheckCircle2,
  ZoomIn, ZoomOut, Network, Send, User, Sparkles
} from 'lucide-react';
import api from '@/api';
import {
  getInvestigationHistoryItem,
  updateInvestigationHistory,
} from '@/services/historyService';
import {
  FederatedCausalGraph,
  DetailsModal,
  RootCauseGraph
} from './CausalChainAnalysis';

// Status badge component
function StatusBadge({ status }) {
  const configs = {
    inProgress: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Loader2, label: 'Thinking...', animate: true },
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Completed' },
    failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Failed' },
  };

  const config = configs[status] || configs.inProgress;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className={`w-3.5 h-3.5 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

// Helper to check for sample data
const isSampleData = (value) => {
  if (!value) return false;
  if (typeof value === 'string') return value.startsWith('[SAMPLE]');
  return false;
};

// Build node data for RootCauseGraph
// Adapted from CausalChainAnalysis.js
const buildInvestigationNodes = (investigationData) => {
  if (!investigationData) return [];
  const nodes = [];

  const analysis = investigationData.analysis || {};
  const incident = investigationData.incident || investigationData.matched_issue;
  const causalChain = analysis.causal_chain;
  const triggeringChange = analysis.triggering_change;
  const recommendedActions = analysis.recommended_actions || [];
  const sourceType = investigationData.source_type;

  // Incident Node
  if (incident) {
    const props = incident.properties || incident;
    if (props.id && !isSampleData(props.id)) {
      if (sourceType === 'github_pr' || sourceType === 'gitlab_mr') {
        nodes.push({
          type: 'github_pr',
          data: {
            id: props.id,
            title: props.title || `PR #${props.number || '?'}`,
            number: props.number,
            state: props.state || (props.merged ? 'merged' : 'unknown'),
            merged: props.merged,
            author: props.author || props.user?.login || 'Unknown',
            author_avatar: props.author_avatar,
            created_at: props.created_at,
            merged_at: props.merged_at,
            description: props.body || props.description,
            repo: props.repo || props.repo_key,
            url: props.url || props.html_url,
            source_type: sourceType,
            additions: props.additions,
            deletions: props.deletions,
            changed_files: props.changed_files
          }
        });
      } else if (sourceType === 'github_workflow') {
        nodes.push({
          type: 'github_workflow',
          data: {
            id: props.id,
            title: props.workflow_name || props.name || 'Workflow Run',
            run_number: props.run_number,
            conclusion: props.conclusion || props.status || 'unknown',
            branch: props.head_branch || props.branch,
            repo: props.repo || props.repo_key,
            url: props.url || props.html_url,
            source_type: sourceType
          }
        });
      } else {
        nodes.push({
          type: 'incident',
          data: {
            id: props.id,
            title: props.title || 'Unknown Incident',
            severity: props.severity || 'unknown',
            status: props.status || 'Unknown',
            description: props.description,
            impact: props.impact,
            affected_services: props.affected_services,
            linked_resources: props.linked_resources
          }
        });
      }
    }
  }

  // Triggering Change
  if (triggeringChange && triggeringChange.id && !isSampleData(triggeringChange.author)) {
    nodes.push({
      type: 'github_commit',
      data: {
        id: triggeringChange.id,
        sha: triggeringChange.id,
        message: triggeringChange.message,
        title: triggeringChange.message,
        author: triggeringChange.author,
        timestamp: triggeringChange.timestamp,
        time: triggeringChange.time_before_incident_minutes
          ? `${triggeringChange.time_before_incident_minutes}m before incident`
          : undefined,
        files_changed: triggeringChange.files_changed,
        service: triggeringChange.service,
        pr_number: triggeringChange.pr_number,
        change_summary: triggeringChange.change_summary,
        url: triggeringChange.url
      }
    });
  }

  // Root Cause
  if (causalChain?.root_cause && !isSampleData(causalChain.root_cause)) {
    const rootCauseLink = causalChain.links?.find(l => l.target_service === causalChain.root_cause) || causalChain.links?.[0];
    nodes.push({
      type: 'root_cause',
      data: {
        title: rootCauseLink?.evidence || `Issue in ${causalChain.root_cause}`,
        service: causalChain.root_cause,
        confidence: rootCauseLink?.confidence,
        evidence: rootCauseLink?.evidence,
        impact: rootCauseLink?.impact,
        metrics: rootCauseLink?.metrics
      }
    });
  }

  // Metrics (up to 2)
  if (causalChain?.links) {
    causalChain.links.slice(0, 2).forEach((link) => {
      if (link.target_service && link.target_service !== causalChain.root_cause && !isSampleData(link.target_service)) {
        nodes.push({
          type: 'metric',
          data: {
            title: link.signal_type || 'Metric Anomaly',
            service: link.target_service,
            confidence: link.confidence,
            change: link.evidence
          }
        });
      }
    });
  }

  // Suggested Fix
  if (recommendedActions.length > 0) {
    const action = recommendedActions[0];
    nodes.push({
      type: 'suggested_fix',
      data: {
        title: action.description || 'Suggested Fix',
        action: action.description,
        description: action.command ? 'Execute recommended command' : 'Follow recommended action',
        file_reference: action.file_reference,
        estimated_impact: action.estimated_impact,
        options: action.command ? [{ label: 'Apply Fix', command: action.command }] : []
      }
    });
  }

  return nodes;
};

export default function InvestigationPage({
  investigationId,
  historyItem,
  onBack,
  onRerunInvestigation,
}) {
  const [investigation, setInvestigation] = useState(historyItem || null);
  const [loading, setLoading] = useState(!historyItem);
  const [inputValue, setInputValue] = useState('');

  // Modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [selectedNodeType, setSelectedNodeType] = useState(null);

  useEffect(() => {
    // Check if we have a "complete" item (has restorationData)
    const hasRestorationData = historyItem && (historyItem.restorationData || historyItem.restoration_data);

    if (hasRestorationData) {
      setInvestigation(historyItem);
      setLoading(false);
    } else if (historyItem?.id || investigationId) {
      // If we have an ID but no data (or partial data), fetch full item
      loadInvestigation();
    }
  }, [investigationId, historyItem]);

  const loadInvestigation = async () => {
    setLoading(true);
    try {
      const item = await getInvestigationHistoryItem(investigationId);
      if (item) setInvestigation(item);
    } catch (err) {
      console.error('Failed to load investigation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = (node, dataOverride) => {
    // Handle both signatures:
    // 1. (nodeObject) from FederatedCausalGraph
    // 2. (type, data) from RootCauseGraph

    let type, data;

    if (typeof node === 'string') {
      // (type, data) signature
      type = node;
      data = dataOverride;
    } else if (node && node.node_type) {
      // Federated graph node object
      type = node.node_type;
      data = {
        id: node.id,
        title: node.title,
        ...node.properties,
        source: node.source,
        status: node.status,
        author: node.author,
        url: node.url
      };
    } else if (node && node.type) {
      // Pre-formatted node object (e.g. from buildInvestigationNodes)
      type = node.type;
      data = node.data;
    } else {
      // Fallback
      type = 'generic';
      data = node;
    }

    setSelectedNodeData(data);
    setSelectedNodeType(type);
    setDetailsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground font-medium">Reconstructing investigation...</p>
      </div>
    );
  }

  if (!investigation) return null;

  const input = investigation.input || {};
  const outputs = investigation.outputsMeta || investigation.outputs_meta || {};
  const restoration = investigation.restorationData || investigation.restoration_data || {};
  const resultData = restoration.result || {};

  // Federated Graph (all entities)
  const graphData = restoration.graph || resultData.graph || resultData.analysis?.graph;

  // Root Cause Graph (focused story)
  const richNodes = buildInvestigationNodes(resultData);

  // Determine user query text
  const userQuery = input.query || `Investigate ${input.type}: ${input.itemId || input.repoKey || 'Unknown'}`;

  return (
    <div className="flex flex-col h-full bg-background relative">

      {/* Details Modal */}
      <DetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        data={selectedNodeData}
        type={selectedNodeType}
      />

      {/* Header - Minimalist */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border/40 bg-background/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <h1 className="text-sm font-semibold text-foreground">
            {investigation.customTitle || investigation.title || 'Investigation'}
          </h1>
          <StatusBadge status={investigation.status} />
        </div>
        <div className="text-xs text-muted-foreground">
          {new Date(investigation.createdAt || investigation.created_at).toLocaleString()}
        </div>
      </div>

      {/* Chat Content Stream */}
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-10">

          {/* User Message */}
          <div className="flex items-start justify-end gap-3 group">
            <div className="max-w-[80%]">
              <div className="bg-secondary/40 text-foreground px-5 py-3.5 rounded-2xl rounded-tr-sm text-sm leading-relaxed">
                {userQuery}
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 text-white shadow-sm">
              <User className="w-4 h-4" />
            </div>
          </div>

          {/* AI Response */}
          <div className="flex items-start gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="w-8 h-8 flex-shrink-0 rounded-full bg-black flex items-center justify-center shadow-sm mt-1">
              <Sparkles className="w-4 h-4 text-white" />
            </div>

            <div className="flex-1 space-y-5 overflow-hidden w-full">
              <div className="prose prose-sm max-w-none text-foreground leading-relaxed w-full">
                <p className="font-medium text-foreground/90 text-[15px]">
                  I've analyzed the <span className="font-mono bg-secondary/50 px-1 py-0.5 rounded text-xs">{input.type || 'query'}</span> and found the following:
                </p>

                {/* Summary Block */}
                {outputs.summary && (
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {outputs.summary}
                  </p>
                )}

                {/* Root Cause Section */}
                {outputs.rootCause && (
                  <div className="my-4 p-4 bg-orange-50/50 border border-orange-100/50 rounded-xl">
                    <h3 className="text-orange-900 font-semibold mb-1 flex items-center gap-2 text-xs uppercase tracking-wide">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Root Cause Identified
                    </h3>
                    <p className="text-orange-800 m-0 whitespace-pre-wrap">
                      {outputs.rootCause || outputs.root_cause}
                    </p>
                  </div>
                )}

                {/* ROOT CAUSE GRAPH (Rich SVG) */}
                {richNodes.length > 0 && (
                  <div className="not-prose mt-6 mb-8">
                    <RootCauseGraph
                      nodes={richNodes}
                      analysis={outputs.summary}
                      query={userQuery}
                      onNodeClick={handleNodeClick}
                    />
                  </div>
                )}

                {/* FEDERATED GRAPH (Broad Context) */}
                {graphData && (
                  <div className="not-prose mt-6">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Network className="w-4 h-4" />
                      Expanded Context
                    </h3>
                    <FederatedCausalGraph
                      causalGraph={graphData}
                      onNodeClick={handleNodeClick}
                    />
                  </div>
                )}

                {/* Key Entities / Links */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {outputs.keyEntities?.map((entity, i) => (
                    <span key={i} className="px-2.5 py-1 bg-secondary rounded-md text-xs font-medium text-secondary-foreground border border-border/50">
                      {entity}
                    </span>
                  ))}
                  {outputs.keyLinks?.map((link, i) => (
                    <a key={i} href={link} target="_blank" className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-100 flex items-center gap-1 hover:underline">
                      <ExternalLink className="w-3 h-3" />
                      Link {i + 1}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Input Bar */}
      <div className="shrink-0 p-4 bg-background border-t border-border/40">
        <div className="max-w-3xl mx-auto relative">
          <input
            type="text"
            placeholder="Ask a follow-up question..."
            className="w-full pl-5 pr-12 py-3.5 bg-secondary/30 border border-border/50 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary/20 focus:bg-background transition-all shadow-sm text-sm placeholder:text-muted-foreground/60"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${inputValue ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
            disabled={!inputValue}
          >
            <Send className="w-4 h-4" />
          </button>
          <div className="text-[10px] text-center text-muted-foreground/40 mt-2">
            Vedaya AI can make mistakes. Consider checking important information.
          </div>
        </div>
      </div>
    </div>
  );
}
